import { GitBranch, Milestone } from 'lucide-react';
import { Badge, Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Button } from '@vnetwork/ui/primitives';
import { useCareerPaths, type CareerPathStageRow } from '@/api/account-career-paths';
import { formatDate } from '@/lib/format';

interface Props { employeeId: number }

const STAGE_LABEL: Record<string, string> = {
  intern: 'Intern',
  probation: 'Probation',
  official: 'Official',
  offboard: 'Offboard',
};

const STAGE_TONE: Record<string, 'info' | 'warning' | 'success' | 'neutral'> = {
  intern: 'info',
  probation: 'warning',
  official: 'success',
  offboard: 'neutral',
};

/**
 * Career Path tab — reuses the account-career-paths hook, passing the target
 * employee's id instead of the signed-in user's. Read-only stage timeline.
 */
export function EmployeeTabCareerPath({ employeeId }: Props) {
  const { data: stages, isLoading, isError, refetch } = useCareerPaths(employeeId);

  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load career path"
        actions={
          <Button size="sm" variant="ghost" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  if (isLoading) {
    return (
      <Card flat>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 64 }} />
          ))}
        </CardBody>
      </Card>
    );
  }

  if (!stages || stages.length === 0) {
    return (
      <EmptyState
        icon={<Milestone size={24} />}
        title="No career-path record"
        description="No employment stages have been recorded for this employee."
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      {stages.map((stage) => (
        <StageCard key={stage.career_path_id} stage={stage} />
      ))}
    </div>
  );
}

function StageCard({ stage }: { stage: CareerPathStageRow }) {
  const label = STAGE_LABEL[stage.career_path_stage] ?? stage.career_path_stage;
  const tone = STAGE_TONE[stage.career_path_stage] ?? 'neutral';
  const start = formatDate(stage.career_path_start_date);
  const end = stage.career_path_end_date ? formatDate(stage.career_path_end_date) : 'Present';

  return (
    <Card>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
          <Badge tone={tone} appearance="subtle" startIcon={<GitBranch size={13} />}>
            {label}
          </Badge>
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            {start} — {end}
          </span>
        </div>

        {stage.career_path_note ? (
          <p style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-default)' }}>
            {stage.career_path_note}
          </p>
        ) : null}

        {stage.notes && stage.notes.length > 0 ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            {stage.notes.map((note, i) => (
              <div key={i} style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                • {note.note_content}
                {note.created_by_name ? (
                  <span style={{ color: 'var(--vn-fg-subtle)' }}> — {note.created_by_name}</span>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
